import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import DecisionDisplay from "./DecisionDisplay";
import LoadingSpinner from "./LoadingSpinner";
import RulesEditor from "./RulesEditor";
import "./DisputeForm.css";

interface EvidenceItem {
  type:
    | "photo"
    | "pdf"
    | "video"
    | "tracking_log"
    | "API_data"
    | "text_note"
    | "youtube_video";
  description: string;
  timestamp: string;
  sha256_hash: string;
  source: "Buyer" | "Seller";
  file?: File;
  fileName?: string;
  // YouTube URL field
  youtubeUrl?: string;
  // AI Analysis fields
  aiAnalysis?: string;
  analysisConfidence?: number;
  extractedText?: string;
  analysisSuccess?: boolean;
  isAnalyzing?: boolean;
  // Preview fields
  previewUrl?: string;
  previewText?: string;
  // Cloudinary storage fields
  cloudinaryUrl?: string;
  cloudinaryPublicId?: string;
  cloudinaryFormat?: string;
  fileSize?: number;
}

interface DisputeFormData {
  request_details?: string;
  claimant_type: "Buyer" | "Seller";
  dispute_amount: string;
  statement_of_claim: string;
  statement_of_defence: string;
  dispute_category: string;
  submitted_evidence: EvidenceItem[];
  custom_rules?: string;
}

interface DisputeDecision {
  decision: {
    dispute_id: string;
    dispute_category: string;
    rules_applied: string[];
    confidence_score: number;
    finding_summary: string;
    remedy_awarded: {
      type: string;
      amount_usd: number;
      awarded_to: "Buyer" | "Seller" | null;
      return_required: boolean;
      notes?: string;
    };
    compliance_deadline: string;
    misconduct_flag: {
      misleading_conduct: boolean;
      fraudulent_behavior: boolean;
      tier: string | null;
    };
    appealable: boolean;
  };
}

const DisputeForm: React.FC = () => {
  const location = useLocation();

  const [formData, setFormData] = useState<DisputeFormData>({
    request_details: "",
    claimant_type: "Buyer",
    dispute_amount: "",
    statement_of_claim: "",
    statement_of_defence: "",
    dispute_category: "",
    submitted_evidence: [],
  });

  const [loading, setLoading] = useState(false);
  const [decision, setDecision] = useState<DisputeDecision | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Rules Editor State
  const [customRules, setCustomRules] = useState<string>("");
  const [useCustomRules, setUseCustomRules] = useState<boolean>(false);

  // Handle restore data from admin dashboard
  useEffect(() => {
    const state = location.state as any;
    if (state?.restoreData) {
      const {
        claimantType,
        disputeAmount,
        statementOfClaim,
        statementOfDefence,
        disputeCategory,
        requestDetails,
        evidence,
      } = state.restoreData;

      setFormData({
        claimant_type: claimantType || "Buyer",
        dispute_amount: disputeAmount?.toString() || "",
        statement_of_claim: statementOfClaim || "",
        statement_of_defence: statementOfDefence || "",
        dispute_category: disputeCategory || "",
        request_details: requestDetails || "",
        submitted_evidence: evidence || [],
      });

      // Clear the location state to prevent re-restoration on re-render
      window.history.replaceState({}, document.title);

      // Scroll to top
      window.scrollTo(0, 0);
    }
  }, [location]);

  // Cleanup effect for preview URLs
  useEffect(() => {
    return () => {
      // Clean up all preview URLs when component unmounts
      formData.submitted_evidence.forEach((evidence) => {
        if (evidence.previewUrl) {
          URL.revokeObjectURL(evidence.previewUrl);
        }
      });
    };
  }, [formData.submitted_evidence]);

  const evidenceTypes = [
    "photo",
    "pdf",
    "video",
    "tracking_log",
    "API_data",
    "text_note",
    "youtube_video",
  ] as const;

  const generateSHA256 = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  };

  const validateFile = (file: File, evidenceType: string): string | null => {
    // Larger size limit for video files
    const maxSize = file.type.startsWith("video/")
      ? 200 * 1024 * 1024 // 200MB for videos
      : 10 * 1024 * 1024; // 10MB for other files

    if (file.size > maxSize) {
      const maxSizeMB = maxSize / 1024 / 1024;
      return `File size must be less than ${maxSizeMB}MB`;
    }

    const allowedTypes: Record<string, string[]> = {
      photo: ["image/jpeg", "image/png", "image/gif", "image/webp"],
      pdf: ["application/pdf"],
      video: [
        "video/mp4",
        "video/mpeg",
        "video/mov",
        "video/avi",
        "video/webm",
        "video/quicktime",
        "video/x-flv",
        "video/mpg",
        "video/wmv",
        "video/3gpp",
      ],
      tracking_log: ["text/plain", "text/csv", "application/json"],
      API_data: ["application/json", "text/plain"],
      text_note: ["text/plain", "text/markdown"],
    };

    if (
      allowedTypes[evidenceType] &&
      !allowedTypes[evidenceType].includes(file.type)
    ) {
      return `Invalid file type for ${evidenceType}. Allowed types: ${allowedTypes[
        evidenceType
      ].join(", ")}`;
    }

    return null;
  };

  const isValidYouTubeUrl = (url: string): boolean => {
    const youtubeRegex =
      /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|v\/)|youtu\.be\/)[\w-]+/;
    return youtubeRegex.test(url);
  };

  const generateSHA256FromString = async (text: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const addEvidenceItem = () => {
    const newEvidence: EvidenceItem = {
      type: "photo",
      description: "",
      timestamp: new Date().toISOString(),
      sha256_hash: "",
      source: "Buyer",
    };

    setFormData((prev) => ({
      ...prev,
      submitted_evidence: [...prev.submitted_evidence, newEvidence],
    }));
  };

  const removeEvidenceItem = (index: number) => {
    // Clean up preview URL if it exists
    const evidence = formData.submitted_evidence[index];
    if (evidence.previewUrl) {
      URL.revokeObjectURL(evidence.previewUrl);
    }

    setFormData((prev) => ({
      ...prev,
      submitted_evidence: prev.submitted_evidence.filter((_, i) => i !== index),
    }));
  };

  const handleEvidenceChange = (
    index: number,
    field: keyof EvidenceItem,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      submitted_evidence: prev.submitted_evidence.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const handleFileUpload = async (index: number, file: File) => {
    console.log("=== FILE UPLOAD STARTED ===");
    console.log("Evidence Index:", index);
    console.log("File Name:", file.name);
    console.log("File Size:", file.size, "bytes");
    console.log("File Type:", file.type);
    console.log(
      "File Last Modified:",
      new Date(file.lastModified).toISOString()
    );

    const evidence = formData.submitted_evidence[index];
    console.log("Evidence Type:", evidence.type);
    console.log("Evidence Source:", evidence.source);

    const validationError = validateFile(file, evidence.type);

    if (validationError) {
      console.error("File validation failed:", validationError);
      alert(validationError);
      return;
    }

    // Clean up old preview URL if it exists
    if (evidence.previewUrl) {
      URL.revokeObjectURL(evidence.previewUrl);
    }

    // Declare preview variables at function scope
    let previewUrl: string | undefined;
    let previewText: string | undefined;

    try {
      // Step 1: Set analyzing state and generate SHA256 hash
      console.log("Generating SHA256 hash...");
      const sha256Hash = await generateSHA256(file);
      console.log("SHA256 Hash generated:", sha256Hash);

      // Generate preview based on file type
      if (file.type.startsWith("image/")) {
        // For images, create an object URL
        previewUrl = URL.createObjectURL(file);
      } else if (file.type === "application/pdf") {
        // For PDFs, create an object URL for iframe preview
        previewUrl = URL.createObjectURL(file);
      } else if (file.type.startsWith("video/")) {
        // For videos, create an object URL for video preview
        previewUrl = URL.createObjectURL(file);
      } else if (
        file.type.startsWith("text/") ||
        file.type === "application/json"
      ) {
        // For text files, read the content
        const text = await file.text();
        previewText = text.length > 500 ? text.substring(0, 500) + "..." : text;
      }

      // Set the analyzing state to show spinner
      setFormData((prev) => ({
        ...prev,
        submitted_evidence: prev.submitted_evidence.map((item, i) =>
          i === index
            ? {
                ...item,
                file: file,
                fileName: file.name,
                sha256_hash: sha256Hash,
                isAnalyzing: true,
                aiAnalysis: "",
                analysisConfidence: 0,
                extractedText: "",
                analysisSuccess: false,
                previewUrl: previewUrl,
                previewText: previewText,
              }
            : item
        ),
      }));

      // Step 2: Upload file to server for AI analysis
      console.log("Uploading file to server for AI analysis...");

      const formDataUpload = new FormData();
      formDataUpload.append("evidenceFile", file);
      formDataUpload.append("evidenceType", evidence.type);
      formDataUpload.append("description", evidence.description);
      formDataUpload.append("source", evidence.source);
      // Use top-level Request Context for all evidence uploads
      if (!formData.request_details || formData.request_details.trim() === "") {
        throw new Error(
          "Request Context is required. Please fill it at the top of the form."
        );
      }
      formDataUpload.append("request_context", formData.request_details);

      const analysisResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/files/analyze`,
        formDataUpload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("✅ Server analysis completed:");
      console.log("- Analysis success:", analysisResponse.data.success);
      console.log("- File info:", analysisResponse.data.fileInfo);
      console.log(
        "- Analysis confidence:",
        analysisResponse.data.analysis?.confidence
      );
      console.log(
        "- Analysis length:",
        analysisResponse.data.analysis?.analysis?.length,
        "characters"
      );

      // Step 3: Update form data with analysis results
      setFormData((prev) => ({
        ...prev,
        submitted_evidence: prev.submitted_evidence.map((item, i) =>
          i === index
            ? {
                ...item,
                file: file,
                fileName: file.name,
                sha256_hash: sha256Hash,
                isAnalyzing: false,
                // Add AI analysis results
                aiAnalysis: analysisResponse.data.analysis?.analysis || "",
                analysisConfidence:
                  analysisResponse.data.analysis?.confidence || 0,
                extractedText:
                  analysisResponse.data.analysis?.extractedText || "",
                analysisSuccess:
                  analysisResponse.data.analysis?.success || false,
                previewUrl: previewUrl,
                previewText: previewText,
                // Add Cloudinary storage data if available
                cloudinaryUrl:
                  analysisResponse.data.evidenceData?.cloudinaryUrl,
                cloudinaryPublicId:
                  analysisResponse.data.evidenceData?.cloudinaryPublicId,
                cloudinaryFormat:
                  analysisResponse.data.evidenceData?.cloudinaryFormat,
                fileSize: analysisResponse.data.evidenceData?.fileSize,
              }
            : item
        ),
      }));

      console.log("✅ File upload and analysis completed successfully");
      console.log("=============================================\n");
    } catch (error) {
      console.error("❌ Error during file upload/analysis:", error);

      // Fallback: just store file locally if server analysis fails
      try {
        const sha256Hash = await generateSHA256(file);
        setFormData((prev) => ({
          ...prev,
          submitted_evidence: prev.submitted_evidence.map((item, i) =>
            i === index
              ? {
                  ...item,
                  file: file,
                  fileName: file.name,
                  sha256_hash: sha256Hash,
                  isAnalyzing: false,
                  aiAnalysis:
                    "⚠️ Server analysis failed - file stored locally only. The file will still be included as evidence but without AI analysis.",
                  analysisConfidence: 0,
                  extractedText: "",
                  analysisSuccess: false,
                  previewUrl: previewUrl,
                  previewText: previewText,
                }
              : item
          ),
        }));
      } catch (fallbackError) {
        console.error("Fallback processing also failed:", fallbackError);
        setFormData((prev) => ({
          ...prev,
          submitted_evidence: prev.submitted_evidence.map((item, i) =>
            i === index
              ? {
                  ...item,
                  isAnalyzing: false,
                  aiAnalysis:
                    "❌ Error processing file. Please try uploading again.",
                  analysisConfidence: 0,
                  extractedText: "",
                  analysisSuccess: false,
                  previewUrl: previewUrl,
                  previewText: previewText,
                }
              : item
          ),
        }));
      }
    }
  };

  const handleYouTubeAnalysis = async (index: number, youtubeUrl: string) => {
    console.log("=== YOUTUBE URL ANALYSIS STARTED ===");
    console.log("Evidence Index:", index);
    console.log("YouTube URL:", youtubeUrl);

    // Validate YouTube URL
    if (!isValidYouTubeUrl(youtubeUrl)) {
      alert(
        "Invalid YouTube URL format. Please provide a valid YouTube video URL."
      );
      return;
    }

    const evidence = formData.submitted_evidence[index];
    console.log("Evidence Type:", evidence.type);
    console.log("Evidence Source:", evidence.source);

    try {
      // Set the analyzing state to show spinner
      console.log("Generating SHA256 hash from URL...");
      const sha256Hash = await generateSHA256FromString(youtubeUrl);
      console.log("SHA256 Hash generated:", sha256Hash);

      setFormData((prev) => ({
        ...prev,
        submitted_evidence: prev.submitted_evidence.map((item, i) =>
          i === index
            ? {
                ...item,
                youtubeUrl: youtubeUrl,
                fileName: youtubeUrl,
                sha256_hash: sha256Hash,
                isAnalyzing: true,
                aiAnalysis: "",
                analysisConfidence: 0,
                extractedText: "",
                analysisSuccess: false,
              }
            : item
        ),
      }));

      // Send to server for AI analysis
      console.log("Sending YouTube URL to server for AI analysis...");

      // Check if request context is filled
      if (!formData.request_details || formData.request_details.trim() === "") {
        throw new Error(
          "Request Context is required. Please fill it at the top of the form."
        );
      }

      const requestBody = {
        youtubeUrl: youtubeUrl,
        evidenceType: evidence.type,
        description: evidence.description,
        source: evidence.source,
        request_context: formData.request_details,
      };

      const analysisResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/files/analyze-youtube`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("✅ YouTube analysis completed:");
      console.log("- Analysis success:", analysisResponse.data.success);
      console.log(
        "- Analysis confidence:",
        analysisResponse.data.analysis?.confidence
      );
      console.log(
        "- Analysis length:",
        analysisResponse.data.analysis?.analysis?.length,
        "characters"
      );

      // Update form data with analysis results
      setFormData((prev) => ({
        ...prev,
        submitted_evidence: prev.submitted_evidence.map((item, i) =>
          i === index
            ? {
                ...item,
                youtubeUrl: youtubeUrl,
                fileName: youtubeUrl,
                sha256_hash: sha256Hash,
                isAnalyzing: false,
                aiAnalysis: analysisResponse.data.analysis?.analysis || "",
                analysisConfidence:
                  analysisResponse.data.analysis?.confidence || 0,
                extractedText:
                  analysisResponse.data.analysis?.extractedText || "",
                analysisSuccess:
                  analysisResponse.data.analysis?.success || false,
              }
            : item
        ),
      }));

      console.log("✅ YouTube URL analysis completed successfully");
      console.log("=============================================\n");
    } catch (error: any) {
      console.error("❌ Error during YouTube URL analysis:", error);

      // Fallback: show error message
      setFormData((prev) => ({
        ...prev,
        submitted_evidence: prev.submitted_evidence.map((item, i) =>
          i === index
            ? {
                ...item,
                isAnalyzing: false,
                aiAnalysis:
                  error.response?.data?.message ||
                  "❌ Error analyzing YouTube video. Please verify the URL is correct and the video is public.",
                analysisConfidence: 0,
                extractedText: "",
                analysisSuccess: false,
              }
            : item
        ),
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setDecision(null);

    console.log("=== DISPUTE FORM SUBMISSION ===");
    console.log("Timestamp:", new Date().toISOString());
    console.log("Form Data Overview:");
    console.log("- Claimant Type:", formData.claimant_type);
    console.log("- Dispute Amount:", formData.dispute_amount);
    console.log("- Category:", formData.dispute_category);
    console.log(
      "- Statement of Claim Length:",
      formData.statement_of_claim.length
    );
    console.log(
      "- Statement of Defence Length:",
      formData.statement_of_defence.length
    );
    console.log("- Evidence Items:", formData.submitted_evidence.length);
    console.log("- Custom Rules:", useCustomRules ? "YES" : "NO");
    if (useCustomRules && customRules) {
      console.log("- Custom Rules Length:", customRules.length, "characters");
      console.log(
        "- Custom Rules Preview:",
        customRules.substring(0, 200) + "..."
      );
    }

    // Log each evidence item in detail
    formData.submitted_evidence.forEach((item, index) => {
      console.log(`\nFrontend Evidence Item ${index + 1}:`);
      console.log("- Type:", item.type);
      console.log("- Description:", item.description);
      console.log("- Source:", item.source);
      console.log("- Timestamp:", item.timestamp);
      console.log("- SHA256 Hash:", item.sha256_hash);
      console.log("- File Name:", item.fileName);
      console.log("- Has File Object:", !!item.file);

      if (item.file) {
        console.log("- File Size:", item.file.size);
        console.log("- File Type:", item.file.type);
        console.log(
          "- File Last Modified:",
          new Date(item.file.lastModified).toISOString()
        );

        // Check if File object will be serialized
        console.log(
          "- File serialization test:",
          JSON.stringify(item.file) === "{}"
            ? "File object will NOT be serialized"
            : "File object will be serialized"
        );
      }
    });

    // Prepare the data that will be sent
    const submitData = {
      ...formData,
      custom_rules: useCustomRules ? customRules : undefined,
      startTime: Date.now(),
    };

    console.log("\n=== DATA BEING SENT TO SERVER ===");
    console.log("Payload size:", JSON.stringify(submitData).length, "bytes");
    console.log(
      "Serialized submitted_evidence:",
      JSON.stringify(submitData.submitted_evidence, null, 2)
    );
    console.log("================================\n");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/dispute`,
        submitData
      );

      console.log("✅ Server response received successfully");
      console.log("Response data keys:", Object.keys(response.data));
      setDecision(response.data);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.details ||
        err.message ||
        "Failed to process dispute. Please try again.";
      setError(errorMessage);
      console.error("❌ Dispute submission error:", err);
      console.error("Error response:", err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dispute-form-container">
      <LoadingSpinner isVisible={loading} />
      <form onSubmit={handleSubmit} className="dispute-form">
        <div className="form-group">
          <label htmlFor="request_details">
            Request Context <span style={{ color: "red" }}>*</span>
          </label>
          <textarea
            id="request_details"
            name="request_details"
            value={formData.request_details}
            onChange={handleChange}
            placeholder="Describe the original request this evidence must fulfill (e.g., 'Take a photo of yourself wearing a blue shirt')"
            rows={3}
            maxLength={2000}
            required
          />
          <div className="char-count">
            {formData.request_details?.length || 0}/2000 characters
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="claimant_type">Claimant Type</label>
          <select
            id="claimant_type"
            name="claimant_type"
            value={formData.claimant_type}
            onChange={handleChange}
            required
          >
            <option value="Buyer">Buyer</option>
            <option value="Seller">Seller</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="dispute_amount">Dispute Amount (USD)</label>
          <input
            type="number"
            id="dispute_amount"
            name="dispute_amount"
            value={formData.dispute_amount}
            onChange={handleChange}
            required
            min="0.01"
            max="1000.00"
            step="0.01"
            placeholder="e.g., 99.99"
          />
        </div>

        <div className="form-group">
          <label htmlFor="dispute_category">Dispute Category</label>
          <textarea
            id="dispute_category"
            name="dispute_category"
            value={formData.dispute_category}
            onChange={handleChange}
            required
            rows={2}
            maxLength={500}
            placeholder="Describe the category of dispute (e.g., Item not received, Item defective or damaged, etc.)"
          />
          <div className="char-count">
            {formData.dispute_category.length}/500
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="statement_of_claim">Statement of Claim</label>
          <textarea
            id="statement_of_claim"
            name="statement_of_claim"
            value={formData.statement_of_claim}
            onChange={handleChange}
            required
            rows={8}
            maxLength={2000}
            placeholder="Describe the buyer's or seller's claim (max 2000 words)"
          />
          <div className="char-count">
            {formData.statement_of_claim.length}/2000
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="statement_of_defence">
            Statement of Defence (optional)
          </label>
          <textarea
            id="statement_of_defence"
            name="statement_of_defence"
            value={formData.statement_of_defence}
            onChange={handleChange}
            rows={6}
            placeholder="Describe the opposing party's defence (if provided)"
          />
        </div>

        {/* Rules Editor Section */}
        <RulesEditor
          customRules={customRules}
          onRulesChange={setCustomRules}
          useCustomRules={useCustomRules}
          onToggleCustomRules={setUseCustomRules}
        />

        <div className="form-group">
          <label>Submitted Evidence</label>
          <div className="evidence-section">
            {formData.submitted_evidence.map((evidence, index) => (
              <div key={index} className="evidence-item">
                <div className="evidence-header">
                  <h4>Evidence Item {index + 1}</h4>
                  <button
                    type="button"
                    onClick={() => removeEvidenceItem(index)}
                    className="remove-evidence-btn"
                  >
                    ×
                  </button>
                </div>

                <div className="evidence-fields">
                  <div className="evidence-field">
                    <label>Type</label>
                    <select
                      value={evidence.type}
                      onChange={(e) =>
                        handleEvidenceChange(index, "type", e.target.value)
                      }
                      required
                    >
                      {evidenceTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="evidence-field">
                    <label>Description</label>
                    <input
                      type="text"
                      value={evidence.description}
                      onChange={(e) =>
                        handleEvidenceChange(
                          index,
                          "description",
                          e.target.value
                        )
                      }
                      required
                      placeholder="Describe the evidence"
                    />
                  </div>

                  {/* Request Context removed here; it is now a single required field at the top of the form */}

                  <div className="evidence-field">
                    <label>Timestamp</label>
                    <input
                      type="datetime-local"
                      value={evidence.timestamp.slice(0, 16)}
                      onChange={(e) =>
                        handleEvidenceChange(
                          index,
                          "timestamp",
                          new Date(e.target.value).toISOString()
                        )
                      }
                      required
                    />
                  </div>

                  {/* Conditional rendering based on evidence type */}
                  {evidence.type === "youtube_video" ? (
                    <div className="evidence-field">
                      <label>YouTube URL</label>
                      <div className="youtube-url-container">
                        <input
                          type="url"
                          value={evidence.youtubeUrl || ""}
                          onChange={(e) =>
                            handleEvidenceChange(
                              index,
                              "youtubeUrl",
                              e.target.value
                            )
                          }
                          placeholder="https://www.youtube.com/watch?v=..."
                          className="youtube-url-input"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (evidence.youtubeUrl) {
                              handleYouTubeAnalysis(index, evidence.youtubeUrl);
                            } else {
                              alert("Please enter a YouTube URL first");
                            }
                          }}
                          className="analyze-youtube-btn"
                          disabled={
                            evidence.isAnalyzing || !evidence.youtubeUrl
                          }
                        >
                          {evidence.isAnalyzing
                            ? "Analyzing..."
                            : "🎬 Analyze Video"}
                        </button>
                      </div>
                      <small style={{ color: "#666", fontSize: "12px" }}>
                        Supported formats: youtube.com/watch?v=, youtu.be/,
                        youtube.com/embed/
                      </small>
                    </div>
                  ) : (
                    <div className="evidence-field">
                      <label>Upload File</label>
                      <div className="file-upload-container">
                        <input
                          type="file"
                          id={`file-upload-${index}`}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleFileUpload(index, file);
                            }
                          }}
                          className="file-input"
                          accept={
                            evidence.type === "photo"
                              ? "image/*"
                              : evidence.type === "pdf"
                              ? ".pdf"
                              : evidence.type === "video"
                              ? "video/*"
                              : evidence.type === "tracking_log"
                              ? ".txt,.csv,.json"
                              : evidence.type === "API_data"
                              ? ".json,.txt"
                              : ".txt,.md"
                          }
                        />
                        <label
                          htmlFor={`file-upload-${index}`}
                          className="file-upload-btn"
                        >
                          📁 Choose File
                        </label>
                        {evidence.fileName && !evidence.youtubeUrl && (
                          <span className="file-name">{evidence.fileName}</span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="evidence-field">
                    <label>SHA256 Hash</label>
                    <input
                      type="text"
                      value={evidence.sha256_hash}
                      onChange={(e) =>
                        handleEvidenceChange(
                          index,
                          "sha256_hash",
                          e.target.value
                        )
                      }
                      required
                      placeholder={
                        evidence.fileName
                          ? "Auto-generated from uploaded file"
                          : "SHA256 hash of the evidence file"
                      }
                      readOnly={!!evidence.fileName}
                    />
                  </div>

                  <div className="evidence-field">
                    <label>Source</label>
                    <select
                      value={evidence.source}
                      onChange={(e) =>
                        handleEvidenceChange(index, "source", e.target.value)
                      }
                      required
                    >
                      <option value="Buyer">Buyer</option>
                      <option value="Seller">Seller</option>
                    </select>
                  </div>
                </div>

                {/* File Preview Section */}
                {(evidence.previewUrl ||
                  evidence.previewText ||
                  evidence.youtubeUrl) && (
                  <div className="file-preview-section">
                    <h4>
                      📄{" "}
                      {evidence.type === "youtube_video"
                        ? "Video Preview"
                        : "File Preview"}
                    </h4>
                    {evidence.type === "youtube_video" &&
                      evidence.youtubeUrl && (
                        <div className="youtube-preview">
                          <iframe
                            width="100%"
                            height="400"
                            src={evidence.youtubeUrl
                              .replace(/watch\?v=/, "embed/")
                              .replace(/youtu\.be\//, "youtube.com/embed/")}
                            title="YouTube video preview"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{
                              borderRadius: "8px",
                              border: "1px solid #ddd",
                            }}
                          ></iframe>
                        </div>
                      )}
                    {evidence.previewUrl && evidence.type === "photo" && (
                      <div className="image-preview">
                        <img
                          src={evidence.previewUrl}
                          alt="Evidence preview"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "400px",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                    )}
                    {evidence.previewUrl && evidence.type === "pdf" && (
                      <div className="pdf-preview">
                        <iframe
                          src={evidence.previewUrl}
                          style={{
                            width: "100%",
                            height: "500px",
                            border: "1px solid #ddd",
                          }}
                          title="PDF Preview"
                        />
                      </div>
                    )}
                    {evidence.previewUrl && evidence.type === "video" && (
                      <div className="video-preview">
                        <video
                          src={evidence.previewUrl}
                          controls
                          style={{
                            width: "100%",
                            maxHeight: "500px",
                          }}
                        >
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    )}
                    {evidence.previewText && (
                      <div className="text-preview">
                        <pre
                          style={{
                            whiteSpace: "pre-wrap",
                            backgroundColor: "#f5f5f5",
                            padding: "12px",
                            borderRadius: "4px",
                            maxHeight: "300px",
                            overflow: "auto",
                            fontSize: "12px",
                          }}
                        >
                          {evidence.previewText}
                        </pre>
                      </div>
                    )}
                  </div>
                )}

                {/* AI Analysis Results Section */}
                {(evidence.isAnalyzing || evidence.aiAnalysis) && (
                  <div className="ai-analysis-section">
                    <h4>🤖 AI Analysis</h4>

                    {evidence.isAnalyzing ? (
                      <div className="analysis-loading">
                        <div className="analysis-spinner"></div>
                        <p>Analyzing file content with AI...</p>
                      </div>
                    ) : (
                      <div className="analysis-results">
                        {evidence.analysisSuccess && (
                          <div className="analysis-confidence">
                            <span className="confidence-label">
                              Confidence Score:
                            </span>
                            <span
                              className={`confidence-value ${
                                (evidence.analysisConfidence || 0) > 0.8
                                  ? "high"
                                  : (evidence.analysisConfidence || 0) > 0.6
                                  ? "medium"
                                  : "low"
                              }`}
                            >
                              {(
                                (evidence.analysisConfidence || 0) * 100
                              ).toFixed(0)}
                              %
                            </span>
                          </div>
                        )}

                        <div className="analysis-content">
                          <label>Analysis Results:</label>
                          <div className="analysis-text">
                            {evidence.aiAnalysis}
                          </div>
                        </div>

                        {evidence.extractedText && (
                          <div className="extracted-data">
                            <label>Extracted Data:</label>
                            <div className="extracted-text">
                              {evidence.extractedText}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={addEvidenceItem}
              className="add-evidence-btn"
            >
              + Add Evidence Item
            </button>
          </div>
        </div>

        <button type="submit" disabled={loading} className="submit-button">
          {loading ? "Processing..." : "Submit Dispute"}
        </button>
      </form>

      {error && <div className="error-message">{error}</div>}

      {decision && <DecisionDisplay decision={decision} />}
    </div>
  );
};

export default DisputeForm;
